<template>
  <div v-if="showCurrencySelect" class="navbar-currency-dropdown-root">
    <v-menu v-model="menuOpen" location="bottom end" :offset="[0, 8]" :close-on-content-click="false">
      <template #activator="{ props: menuProps }">
        <v-btn
          v-bind="menuProps"
          variant="text"
          color="secondary"
          size="small"
          class="navbar-currency-trigger"
          :loading="saving"
          :disabled="saving"
          aria-label="Display currency"
        >
          <span class="navbar-currency-trigger__code">{{ modelValue }}</span>
          <v-icon icon="expand_more" size="18" class="navbar-currency-trigger__chev" />
        </v-btn>
      </template>

      <v-card class="navbar-currency-panel" elevation="4">
        <v-card-text class="pa-2">
          <v-text-field
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
            hide-details
            class="navbar-currency-panel__search mb-2"
          />
          <div class="navbar-currency-panel__list">
            <button
              v-for="opt in filteredOptions"
              :key="opt.value"
              type="button"
              class="navbar-currency-option"
              :class="{ 'navbar-currency-option--active': opt.value === modelValue }"
              @click="pick(opt.value)"
            >
              {{ opt.value }}
            </button>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useSnackbar } from '@/composables/ui'
import { useAuthStore } from '../../../stores/auth-store'
import { authService } from '../../../services/auth'
import { apiService } from '../../../services/api'
import { iso4217CurrencyCodeOptions } from '../../../constants/iso4217-currencies'

const authStore = useAuthStore()
const { init: notify } = useSnackbar()
const saving = ref(false)
const menuOpen = ref(false)
const searchQuery = ref('')

const showCurrencySelect = computed(() => authService.isUserToken())

const modelValue = computed(() => authStore.user?.currency || authStore.shopDetails?.currency || 'USD')

const filteredOptions = computed(() => {
  const q = searchQuery.value.trim().toUpperCase()
  if (!q) return iso4217CurrencyCodeOptions
  return iso4217CurrencyCodeOptions.filter((o) => o.value.includes(q) || o.label.toUpperCase().includes(q))
})

watch(menuOpen, (open) => {
  if (!open) searchQuery.value = ''
})

async function saveCurrency(code: string | undefined) {
  if (!code || saving.value) return
  if (code === modelValue.value) return

  saving.value = true
  try {
    await apiService.updateUser({ currency: code })
    authStore.user = { ...(authStore.user ?? {}), currency: code }
    notify({ message: 'Display currency saved', color: 'success' })
  } catch {
    notify({ message: 'Could not save display currency', color: 'danger' })
  } finally {
    saving.value = false
  }
}

async function pick(code: string) {
  menuOpen.value = false
  await saveCurrency(code)
}
</script>

<style scoped lang="scss">
.navbar-currency-dropdown-root {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
}

.navbar-currency-trigger {
  min-width: 0 !important;
  padding: 0 0.35rem !important;
  text-transform: none;
  letter-spacing: normal;
  font-weight: 600;
}

.navbar-currency-trigger__code {
  font-size: 0.8125rem;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.03em;
  line-height: 1.2;
}

.navbar-currency-trigger__chev {
  flex-shrink: 0;
  opacity: 0.85;
  margin-left: 0.125rem;
}

.navbar-currency-panel {
  width: 7rem;
  min-width: 7rem;
  max-width: min(7rem, calc(100vw - 1rem));
}

.navbar-currency-panel__list {
  max-height: min(16rem, 50vh);
  overflow-y: auto;
}

.navbar-currency-option {
  display: block;
  width: 100%;
  text-align: center;
  font-size: 0.8125rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  padding: 0.35rem 0.25rem;
  margin: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: rgb(var(--v-theme-text-primary));
  cursor: pointer;
}

.navbar-currency-option:hover {
  background: rgb(var(--v-theme-background-element));
}

.navbar-currency-option--active {
  color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-background-element));
}
</style>
